import type { IconName } from '@sentinel/ui';
import type { PermissionKey } from '@sentinel/shared';

/** The two spaces of the Control Center, split by moment (registro I-31): the PCA does what
 *  the running operation needs, the Segreteria prepares before and closes after. Settings
 *  belong to the Segreteria and never show on the PCA. */
export type SpaceKey = 'pca' | 'secretariat';

/** Counters a sidebar entry may carry, always as number and word (rule 13). */
export type NavBadgeKey = 'alarms' | 'teamsWithoutNode' | 'unread' | 'devicesOffline';

export interface NavEntry {
  /** Unique in the space; the label is `shell.nav.entries.<key>`. */
  key: string;
  to: string;
  icon: IconName;
  /** Permission that must be held to see the entry (hiding only: the server re-checks). */
  permission?: PermissionKey;
  /** Other pages of the entry: it lights up there and they belong to its space. */
  also?: readonly string[];
  /** A short line under the label, `shell.nav.subs.<key>` (PCA only). */
  sub?: boolean;
  badge?: NavBadgeKey;
}

export interface NavGroup {
  /** `shell.nav.groups.<key>`; null: no heading (the PCA has none). */
  key: string | null;
  entries: readonly NavEntry[];
}

export interface NavSpace {
  key: SpaceKey;
  icon: IconName;
  groups: readonly NavGroup[];
}

/** PCA: nine big entries without groups in the prototype; MEDEVAC has no page yet and is left
 *  out until its module exists (TODO.md · PCA e Segreteria). */
const PCA: NavSpace = {
  key: 'pca',
  icon: 'target',
  groups: [
    {
      key: null,
      entries: [
        {
          key: 'dashboard',
          to: '/',
          icon: 'dashboard',
          permission: 'operations:view',
          sub: true,
          badge: 'alarms',
        },
        { key: 'map', to: '/map', icon: 'map', permission: 'mapping:view', sub: true },
        {
          key: 'teamsField',
          to: '/teams',
          icon: 'teams',
          permission: 'teams:view',
          also: ['/resources', '/devices'],
          sub: true,
          badge: 'teamsWithoutNode',
        },
        { key: 'field', to: '/field', icon: 'phone', permission: 'devices:view', sub: true },
        {
          key: 'radio',
          to: '/communications',
          icon: 'chat',
          permission: 'communications:view',
          badge: 'unread',
        },
        { key: 'chronicle', to: '/timeline', icon: 'timeline', permission: 'operations:view' },
        { key: 'drones', to: '/drones', icon: 'drone', permission: 'drones:view', sub: true },
        {
          key: 'network',
          to: '/connectivity',
          icon: 'signal',
          permission: 'connectivity:view',
          sub: true,
          badge: 'devicesOffline',
        },
      ],
    },
  ],
};

/** Segreteria: the order of the work, people first, then catalogues, installations, archive
 *  and administration. Pages still to build (Download) are left out until they exist. */
const SECRETARIAT: NavSpace = {
  key: 'secretariat',
  icon: 'operations',
  groups: [
    {
      key: 'people',
      entries: [
        { key: 'checkIn', to: '/personnel', icon: 'user', permission: 'personnel:view' },
        { key: 'teams', to: '/teams', icon: 'teams', permission: 'teams:view' },
        {
          key: 'registry',
          to: '/personnel/registry',
          icon: 'building',
          permission: 'personnel:view',
        },
      ],
    },
    {
      key: 'catalogues',
      entries: [
        { key: 'resources', to: '/resources', icon: 'resources', permission: 'resources:view' },
        { key: 'devices', to: '/devices', icon: 'devices', permission: 'devices:view' },
        {
          key: 'droneRegistry',
          to: '/drones/registry',
          icon: 'drone',
          permission: 'drones:view',
        },
      ],
    },
    {
      key: 'installations',
      entries: [
        {
          key: 'stations',
          to: '/connectivity',
          icon: 'console',
          permission: 'connectivity:view',
        },
      ],
    },
    {
      key: 'archive',
      entries: [
        { key: 'operations', to: '/operations', icon: 'operations', permission: 'operations:view' },
        { key: 'replay', to: '/replay', icon: 'replay', permission: 'tracking:view' },
        { key: 'tracks', to: '/tracks', icon: 'tracks', permission: 'tracking:view' },
        { key: 'reports', to: '/reports', icon: 'report', permission: 'reports:view' },
      ],
    },
    {
      key: 'administration',
      entries: [
        { key: 'settings', to: '/settings', icon: 'settings', permission: 'configuration:view' },
      ],
    },
  ],
};

/** The navigation of the Control Center: one space at a time in the sidebar. */
export const SPACES: readonly NavSpace[] = [PCA, SECRETARIAT];
